/**
 * New site as a page (v3, phase 3b), at /sites/new. Decided in the app UI
 * round (docs/brand/app-ui NOTES.md, screen 3): three tiles on one page, the
 * chosen way opens below, "what happens next" on the right.
 *
 * Only the container changed: the flow is the same CreateFlow (layout="page")
 * with the same site actions (buildSiteCreateActions), the same draft
 * behaviour and the same landing after publishing.
 *
 *   /sites/new                  the three ways
 *   /sites/new?create=agent     opens on "From Claude or ChatGPT" (deep links)
 *   /sites/new?create=convert   opens on Figma, Convert with AI
 *   /sites/new?draft=<id>       resumes an unfinished draft
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import firebase from '../../firebase';
import CreateFlow from '../../components/CreateFlow/CreateFlow';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import GlassButton from '../../components/Aero/GlassButton';
import AeroDotLoader from '../../components/Aero/AeroDotLoader';
import { buildSiteCreateActions } from './createFlowActions';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import './AeroSites.css';

export default function AeroSiteNew() {
  const { user, shell = {} } = useOutletContext();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const create = params.get('create');
  const draftId = params.get('draft');
  const initialSource = create === 'agent' || create === 'convert' ? create : null;

  const actions = useMemo(() => (user?.uid ? buildSiteCreateActions(user.uid) : null), [user]);

  // Resuming a draft: read its doc for the name and the step it was left at,
  // exactly what the Sites list used to hand the modal.
  const [draft, setDraft] = useState(draftId ? undefined : null);
  useEffect(() => {
    if (!draftId || !user?.uid) return undefined;
    let cancelled = false;
    firebase.firestore().collection('user').doc(user.uid).collection('url').doc(draftId).get()
      .then((doc) => {
        if (cancelled) return;
        const d = doc.exists ? doc.data() : null;
        setDraft(d ? { id: draftId, name: d.title || '', draftMeta: d.draftMeta || null } : null);
      })
      .catch(() => { if (!cancelled) setDraft(null); });
    return () => { cancelled = true; };
  }, [draftId, user]);

  const leave = () => {
    if (shell.refresh) shell.refresh();
    navigate('/sites');
  };

  return (
    <div className="aero-main aero-sites-page aero-create-page">
      <Helmet>
        <title>New site | Aero</title>
      </Helmet>
      <AeroPageHeader
        crumbs={[{ label: 'Sites', onClick: () => navigate('/sites') }, { label: 'New site' }]}
        actions={<GlassButton fill="glass" size="sm" onClick={leave}>Cancel</GlassButton>}
      />
      <div className="aero-sd-body">
        {!actions || draft === undefined ? (
          <div className="aero-create-wait"><AeroDotLoader size="md" label="Loading" /></div>
        ) : (
          <CreateFlow
            key={draftId || create || 'new'}
            layout="page"
            user={user}
            kind="site"
            heading="New site"
            existingDraft={draft}
            initialSource={initialSource}
            actions={actions}
            onClose={leave}
            onFinished={(landing) => {
              if (shell.refresh) shell.refresh();
              navigate(landing || '/sites');
            }}
          />
        )}
      </div>
    </div>
  );
}
